import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import {
  buildWorkspaceAgentGuidedVariantSkipNonImagesClause,
  getWorkspaceAgentGuidedVariantImageOutputPhrases,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePromptPhrases';
import {
  hasWorkspaceAgentGuidedVariantChosenImageFormat,
  resolveWorkspaceAgentGuidedVariantPromptRenameSuffix,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantSaveDestination,
  withWorkspaceAgentGuidedVariantSaveDestinationAndRename,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

/** Long axis of the preview vignette, as a percent of the square well. */
const PREVIEW_OVAL_LONG_AXIS_PERCENT = 46;

export type WorkspaceAgentRemoveBackgroundPreviewOval = {
  radiusX: number;
  radiusY: number;
};

/** Ellipse radii that follow the photo. A square well with equal radii is a circle. */
export const getWorkspaceAgentRemoveBackgroundPreviewOval = (
  width: number | null,
  height: number | null,
): WorkspaceAgentRemoveBackgroundPreviewOval => {
  if (!width || !height) {
    return { radiusX: PREVIEW_OVAL_LONG_AXIS_PERCENT, radiusY: PREVIEW_OVAL_LONG_AXIS_PERCENT };
  }

  if (width >= height) {
    return {
      radiusX: PREVIEW_OVAL_LONG_AXIS_PERCENT,
      radiusY: (PREVIEW_OVAL_LONG_AXIS_PERCENT * height) / width,
    };
  }

  return {
    radiusX: (PREVIEW_OVAL_LONG_AXIS_PERCENT * width) / height,
    radiusY: PREVIEW_OVAL_LONG_AXIS_PERCENT,
  };
};

export type BuildWorkspaceAgentRemoveBackgroundPromptInput = {
  backgroundId: WorkspaceAgentGuidedVariantColorOptionId;
  customColor: string;
  destination: WorkspaceAgentGuidedVariantSaveDestination;
  imageCount: number;
  output?: WorkspaceAgentGuidedVariantOutput;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export const buildWorkspaceAgentRemoveBackgroundPrompt = ({
  backgroundId,
  customColor,
  destination,
  imageCount,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  skippedFileCount,
  skippedVideoCount,
}: BuildWorkspaceAgentRemoveBackgroundPromptInput): string => {
  const color = getWorkspaceAgentGuidedVariantCanvasColor(backgroundId, customColor);
  const { subject } = getWorkspaceAgentGuidedVariantImageOutputPhrases(imageCount);
  let suffix = `${backgroundId}-bg`;

  if (backgroundId === 'transparent') {
    suffix = 'no-bg';
  } else if (backgroundId === 'custom') {
    suffix = 'custom-bg';
  }
  const keepOriginalFileType = !hasWorkspaceAgentGuidedVariantChosenImageFormat(output);
  let backgroundInstruction = 'Keep the background transparent and do not composite onto a color.';

  if (color) {
    backgroundInstruction = `After the background is removed, composite the subject onto a solid ${color} canvas.`;
  } else if (keepOriginalFileType) {
    backgroundInstruction =
      'Keep the background transparent (PNG with an alpha channel) and do not composite onto a color.';
  }
  const skipNonImages = buildWorkspaceAgentGuidedVariantSkipNonImagesClause({
    featureAppliesToImagesPhrase: 'remove background only applies to images',
    nonImageFilesWording: 'non-image files',
    skippedFileCount,
    skippedVideoCount,
  });

  return withWorkspaceAgentGuidedVariantSaveDestinationAndRename(
    `Remove the background from ${subject}. ${backgroundInstruction} Do not recreate or reimagine the scene — keep the same subject, pose, composition, framing, and fine detail.${skipNonImages}`,
    destination,
    resolveWorkspaceAgentGuidedVariantPromptRenameSuffix(suffix, output),
  );
};
